<template>
  <div class="conversion-page-container">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Top Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white; font-weight: 500;">
            {{ msg }}
          </span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- 1. HEADER SECTION -->
    <div class="page-hero-header mb-5">
      <div class="header-left-col">
        <PageBreadcrumb :items="[{ text: 'Metal Conversion' }]" class="mb-1" />
        <p class="page-subtitle">
          Track stock item conversions, scrap melt logs, and new ornament creations.
        </p>
      </div>

      <div class="header-right-col">
        <button class="luxury-btn-primary" @click="$router.push('/Conversion')">
          <v-icon size="18" color="#ffffff" class="mr-1">mdi-plus-circle-outline</v-icon>
          <span>New Conversion</span>
        </button>
      </div>
    </div>

    <!-- 2. KPI METRICS CARDS -->
    <div class="metrics-grid mb-5">
      <!-- Total Conversions -->
      <div class="metric-card">
        <div class="metric-card-top">
          <div class="metric-icon-box icon-gold">
            <v-icon size="20" color="#9a6b27">mdi-swap-horizontal-bold</v-icon>
          </div>
          <span class="metric-chip">Entries</span>
        </div>
        <div class="metric-label">TOTAL CONVERSIONS</div>
        <div class="metric-value">{{ list.length }}</div>
        <div class="metric-caption">Logged conversion vouchers</div>
      </div>

      <!-- Total Converted Net Weight -->
      <div class="metric-card">
        <div class="metric-card-top">
          <div class="metric-icon-box icon-emerald">
            <v-icon size="20" color="#16a34a">mdi-scale-balance</v-icon>
          </div>
          <span class="metric-chip emerald">Output</span>
        </div>
        <div class="metric-label">OUTPUT NET WEIGHT</div>
        <div class="metric-value">{{ totalNetWeight.toFixed(2) }} <span class="unit-text">gm</span></div>
        <div class="metric-caption">Total converted product weight</div>
      </div>

      <!-- Raw Input Scrap Weight -->
      <div class="metric-card">
        <div class="metric-card-top">
          <div class="metric-icon-box icon-amber">
            <v-icon size="20" color="#b45309">mdi-gold</v-icon>
          </div>
          <span class="metric-chip amber">Input</span>
        </div>
        <div class="metric-label">INPUT SCRAP WEIGHT</div>
        <div class="metric-value">{{ totalInputWeight.toFixed(2) }} <span class="unit-text">gm</span></div>
        <div class="metric-caption">Total raw material consumed</div>
      </div>

      <!-- Net Loss / Variance -->
      <div class="metric-card">
        <div class="metric-card-top">
          <div class="metric-icon-box icon-blue">
            <v-icon size="20" color="#2563eb">mdi-chart-line</v-icon>
          </div>
          <span class="metric-chip blue">Variance</span>
        </div>
        <div class="metric-label">PROCESS VARIANCE</div>
        <div class="metric-value">{{ weightVariance }} <span class="unit-text">gm</span></div>
        <div class="metric-caption">Scrap melting & refinement delta</div>
      </div>
    </div>

    <!-- 3. SEARCH & FILTER TOOLBAR -->
    <div class="toolbar-panel mb-4">
      <div class="search-input-wrapper">
        <v-icon size="18" color="#94a3b8" class="mr-2">mdi-magnify</v-icon>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search by entry no, raw item, new design, remarks..."
          class="custom-search-input"
        />
        <button v-if="searchQuery" class="clear-search-btn" @click="searchQuery = ''" title="Clear">
          <v-icon size="15" color="#94a3b8">mdi-close</v-icon>
        </button>
      </div>

      <div class="toolbar-actions">
        <!-- Date Quick Filter -->
        <div class="filter-pill-group">
          <button
            class="filter-pill"
            :class="{ active: dateFilter === 'all' }"
            @click="dateFilter = 'all'"
          >
            All
          </button>
          <button
            class="filter-pill"
            :class="{ active: dateFilter === 'month' }"
            @click="dateFilter = 'month'"
          >
            This Month
          </button>
        </div>

        <button class="icon-action-btn" @click="getList" title="Refresh List">
          <v-icon size="18" color="#64748b">mdi-refresh</v-icon>
        </button>
      </div>
    </div>

    <!-- 4. CONVERSIONS TABLE CARD -->
    <div class="table-card">
      <div class="table-container">
        <table class="luxury-table">
          <thead>
            <tr>
              <th class="text-left">ENTRY NO</th>
              <th class="text-left">DATE</th>
              <th class="text-left">FROM (RAW / SCRAP)</th>
              <th class="text-center" style="width: 40px;"></th>
              <th class="text-left">TO (NEW ORNAMENT)</th>
              <th class="text-right">NET WEIGHT</th>
              <th class="text-left">REMARKS</th>
              <th class="text-center" style="width: 110px;">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in paginatedList" :key="item._id || i" class="table-row">
              <!-- Entry No -->
              <td>
                <span class="entry-no-badge">
                  #{{ (item.prefix || "") + (item.entryNo || "") }}
                </span>
              </td>

              <!-- Date -->
              <td>
                <div class="date-cell">
                  <v-icon size="14" color="#94a3b8" class="mr-1">mdi-calendar-blank-outline</v-icon>
                  <span>{{ formatDate(item.date) }}</span>
                </div>
              </td>

              <!-- From (Raw Item) -->
              <td>
                <div class="item-primary-info">
                  <span class="item-title">
                    {{ item.fromItem && item.fromItem.itemName ? item.fromItem.itemName : "Scrap Item" }}
                  </span>
                  <div class="item-meta-row">
                    <span v-if="item.fromItem && item.fromItem.category" class="metal-badge gold">
                      {{ item.fromItem.category }}
                    </span>
                    <span v-if="item.fromItem && item.fromItem.grossWeight" class="meta-wt">
                      Gross: {{ item.fromItem.grossWeight }}g
                    </span>
                  </div>
                </div>
              </td>

              <!-- Arrow icon -->
              <td class="text-center">
                <div class="arrow-indicator">
                  <v-icon size="16" color="#b9893f">mdi-arrow-right</v-icon>
                </div>
              </td>

              <!-- To (New Item) -->
              <td>
                <div class="item-primary-info">
                  <span class="item-title">
                    {{
                      (item.toItem && item.toItem.designId && item.toItem.designId.name) ||
                      (item.toItem && item.toItem.itemName) ||
                      "-"
                    }}
                  </span>
                  <div class="item-meta-row">
                    <span
                      v-if="item.toItem && item.toItem.category"
                      class="metal-badge"
                      :class="getMetalClass(item.toItem.category)"
                    >
                      {{ item.toItem.category }}
                    </span>
                    <span
                      v-if="item.toItem && item.toItem.designId && item.toItem.designId.code"
                      class="design-code-badge"
                    >
                      {{ item.toItem.designId.code }}
                    </span>
                    <span v-if="item.toItems && item.toItems.length > 1" class="design-code-badge">
                      +{{ item.toItems.length - 1 }} more
                    </span>
                  </div>
                </div>
              </td>

              <!-- Net Weight -->
              <td class="text-right">
                <div class="weight-cell">
                  <span class="weight-number">
                    {{ outputNetOf(item).toFixed(2) }}
                  </span>
                  <span class="weight-unit">g</span>
                </div>
              </td>

              <!-- Remarks -->
              <td>
                <span class="remarks-text" :title="item.remarks || ''">
                  {{ item.remarks || "—" }}
                </span>
              </td>

              <!-- Actions -->
              <td class="text-center">
                <div class="actions-group">
                  <button class="row-action-btn view-btn" @click="openViewModal(item)" title="View Conversion Details">
                    <v-icon size="16">mdi-eye-outline</v-icon>
                  </button>
                  <button class="row-action-btn delete-btn" @click="confirmDelete(item)" title="Delete Conversion">
                    <v-icon size="16">mdi-trash-can-outline</v-icon>
                  </button>
                </div>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="filteredList.length === 0">
              <td colspan="8" class="empty-state-cell">
                <div class="empty-state-container">
                  <div class="empty-icon-circle">
                    <v-icon size="36" color="#9a6b27">mdi-swap-horizontal</v-icon>
                  </div>
                  <h3 class="empty-title">No Conversions Found</h3>
                  <p class="empty-desc">
                    {{ searchQuery ? "No results match your search filters." : "Start by recording your first scrap-to-ornament conversion." }}
                  </p>
                  <button v-if="!searchQuery" class="luxury-btn-primary mt-3" @click="$router.push('/Conversion')">
                    <v-icon size="16" color="#ffffff" class="mr-1">mdi-plus</v-icon>
                    <span>Create Conversion</span>
                  </button>
                  <button v-else class="filter-pill active mt-2" @click="searchQuery = ''">
                    Clear Search Filter
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Footer -->
      <div v-if="filteredList.length > 0" class="table-footer-bar">
        <div class="footer-count-text">
          Showing <b>{{ (currentPage - 1) * pageSize + 1 }}</b> -
          <b>{{ Math.min(currentPage * pageSize, filteredList.length) }}</b> of
          <b>{{ filteredList.length }}</b> entries
        </div>

        <div class="pagination-controls" v-if="totalPages > 1">
          <button
            class="page-nav-btn"
            :disabled="currentPage === 1"
            @click="currentPage--"
            title="Previous Page"
          >
            <v-icon size="16">mdi-chevron-left</v-icon>
          </button>
          <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
          <button
            class="page-nav-btn"
            :disabled="currentPage === totalPages"
            @click="currentPage++"
            title="Next Page"
          >
            <v-icon size="16">mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- 5. DETAILS MODAL DIALOG -->
    <v-dialog v-model="viewDialog" max-width="640" content-class="luxury-modal-dialog">
      <div class="dialog-content-card" v-if="selectedItem">
        <div class="dialog-modal-header">
          <div class="d-flex align-center">
            <div class="dialog-header-icon mr-3">
              <v-icon color="#9a6b27" size="22">mdi-swap-horizontal-circle-outline</v-icon>
            </div>
            <div>
              <h2 class="dialog-title">
                Conversion Voucher #{{ (selectedItem.prefix || "") + (selectedItem.entryNo || "") }}
              </h2>
              <span class="dialog-subdate">
                Recorded on {{ formatDate(selectedItem.date) }}
              </span>
            </div>
          </div>
          <button class="dialog-close-btn" @click="viewDialog = false">
            <v-icon size="18" color="#64748b">mdi-close</v-icon>
          </button>
        </div>

        <div class="dialog-body pa-5">
          <!-- Flow Comparison Cards -->
          <div class="flow-cards-grid mb-4">
            <!-- FROM ITEM -->
            <div class="flow-card from-card">
              <div class="flow-card-header">
                <span class="flow-badge from-badge">FROM (RAW / SCRAP)</span>
              </div>
              <div class="flow-item-name">
                {{ selectedItem.fromItem && selectedItem.fromItem.itemName ? selectedItem.fromItem.itemName : "Stock Item" }}
              </div>
              <div class="flow-item-details">
                <div class="detail-row">
                  <span class="detail-label">Category:</span>
                  <span class="detail-val">{{ (selectedItem.fromItem && selectedItem.fromItem.category) || "Gold" }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Gross Wt:</span>
                  <span class="detail-val">{{ selectedItem.fromItem && selectedItem.fromItem.grossWeight ? selectedItem.fromItem.grossWeight + " g" : "-" }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Stone Wt:</span>
                  <span class="detail-val">{{ selectedItem.fromItem && selectedItem.fromItem.stoneWeight ? selectedItem.fromItem.stoneWeight + " g" : "0.00 g" }}</span>
                </div>
                <div class="detail-row highlight">
                  <span class="detail-label">Net Wt:</span>
                  <span class="detail-val bold">{{ selectedItem.fromItem && selectedItem.fromItem.netWeight ? selectedItem.fromItem.netWeight + " g" : "-" }}</span>
                </div>
              </div>
            </div>

            <!-- ARROW IN CENTER -->
            <div class="flow-divider-arrow">
              <v-icon size="24" color="#9a6b27">mdi-arrow-right-bold</v-icon>
            </div>

            <!-- TO ITEM -->
            <div class="flow-card to-card">
              <div class="flow-card-header">
                <span class="flow-badge to-badge">TO (NEW ORNAMENT)</span>
              </div>
              <div class="flow-item-name">
                {{
                  (selectedItem.toItem && selectedItem.toItem.designId && selectedItem.toItem.designId.name) ||
                  (selectedItem.toItem && selectedItem.toItem.itemName) ||
                  "Converted Item"
                }}
              </div>
              <div class="flow-item-details">
                <div class="detail-row">
                  <span class="detail-label">Category:</span>
                  <span class="detail-val">{{ (selectedItem.toItem && selectedItem.toItem.category) || "Gold" }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Gross Wt:</span>
                  <span class="detail-val">{{ selectedItem.toItem && selectedItem.toItem.grossWeight ? selectedItem.toItem.grossWeight + " g" : "-" }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Stone Wt:</span>
                  <span class="detail-val">{{ selectedItem.toItem && selectedItem.toItem.stoneWeight ? selectedItem.toItem.stoneWeight + " g" : "0.00 g" }}</span>
                </div>
                <div class="detail-row" v-if="selectedItem.toItem && selectedItem.toItem.centWeight">
                  <span class="detail-label">Cent Wt:</span>
                  <span class="detail-val">{{ selectedItem.toItem.centWeight }} ct</span>
                </div>
                <div class="detail-row highlight gold-highlight">
                  <span class="detail-label">Net Wt:</span>
                  <span class="detail-val bold">{{ selectedItem.toItem && selectedItem.toItem.netWeight ? selectedItem.toItem.netWeight + " g" : "-" }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Variance Bar -->
          <div class="summary-variance-strip mb-4">
            <div class="variance-item">
              <span class="var-label">Net Input Weight</span>
              <span class="var-val">{{ selectedItem.fromItem ? (selectedItem.fromItem.netWeight || 0) : 0 }} g</span>
            </div>
            <div class="variance-item">
              <span class="var-label">Net Output Weight</span>
              <span class="var-val">{{ outputNetOf(selectedItem).toFixed(3) }} g</span>
            </div>
            <div class="variance-item">
              <span class="var-label">Weight Variance</span>
              <span class="var-val delta">
                {{
                  (
                    (selectedItem.fromItem ? Number(selectedItem.fromItem.netWeight || 0) : 0) -
                    outputNetOf(selectedItem)
                  ).toFixed(3)
                }} g<template v-if="selectedItem.lossPercent"> ({{ selectedItem.lossPercent }}%)</template>
              </span>
            </div>
            <div class="variance-item" v-if="selectedItem.lossValue">
              <span class="var-label">Loss Value</span>
              <span class="var-val">₹ {{ Number(selectedItem.lossValue).toFixed(2) }}</span>
            </div>
          </div>

          <!-- All output items -->
          <div class="mb-4" v-if="selectedItem.toItems && selectedItem.toItems.length > 1">
            <span class="var-label">OUTPUT ITEMS</span>
            <div v-for="(t, ti) in selectedItem.toItems" :key="ti" class="d-flex justify-space-between py-1" style="font-size: 13px; border-bottom: 1px solid #f1f5f9;">
              <span>{{ (t.designId && t.designId.name) || t.itemName }} {{ t.countOrSplit === 'Count' ? '· ' + t.count + ' pcs' : '· by weight' }}</span>
              <span>{{ Number(t.netWeight || 0).toFixed(3) }} g · ₹ {{ Number(t.costPerGram || 0).toFixed(2) }}/g</span>
            </div>
          </div>

          <!-- Remarks note -->
          <div class="dialog-remarks-box">
            <span class="remarks-title">REMARKS:</span>
            <p class="remarks-content">{{ selectedItem.remarks || "No additional remarks entered for this voucher." }}</p>
          </div>
        </div>

        <div class="dialog-modal-footer">
          <button class="filter-pill" @click="viewDialog = false">Close</button>
        </div>
      </div>
    </v-dialog>

    <!-- 6. CONFIRM DELETE DIALOG -->
    <v-dialog v-model="deleteDialog" max-width="440" content-class="luxury-modal-dialog">
      <div class="dialog-content-card pa-5" v-if="itemToDelete">
        <div class="text-center pt-2">
          <div class="warning-icon-circle mx-auto mb-3">
            <v-icon size="32" color="#dc2626">mdi-alert-outline</v-icon>
          </div>
          <h2 class="confirm-dialog-title">Delete Conversion?</h2>
          <p class="confirm-dialog-desc">
            Are you sure you want to delete voucher
            <b>#{{ (itemToDelete.prefix || "") + (itemToDelete.entryNo || "") }}</b>?
            This will reverse the conversion, restore the source item back to stock, and remove the newly created item.
          </p>
        </div>

        <div class="d-flex justify-center gap-3 mt-4">
          <button class="filter-pill mr-2" @click="deleteDialog = false" :disabled="isDeleting">
            Cancel
          </button>
          <button class="delete-confirm-btn" @click="executeDelete" :disabled="isDeleting">
            <v-progress-circular v-if="isDeleting" indeterminate size="16" width="2" color="#ffffff" class="mr-1" />
            <v-icon v-else size="16" color="#ffffff" class="mr-1">mdi-delete</v-icon>
            <span>Confirm Delete</span>
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ConversionList",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      list: [],
      searchQuery: "",
      dateFilter: "all",
      currentPage: 1,
      pageSize: 10,
      viewDialog: false,
      selectedItem: null,
      deleteDialog: false,
      itemToDelete: null,
      isDeleting: false,
    };
  },
  computed: {
    totalNetWeight() {
      return this.list.reduce((sum, item) => {
        const wt = this.outputNetOf(item);
        return sum + (isNaN(wt) ? 0 : wt);
      }, 0);
    },
    totalInputWeight() {
      return this.list.reduce((sum, item) => {
        const wt = item.fromItem && item.fromItem.netWeight ? parseFloat(item.fromItem.netWeight) : 0;
        return sum + (isNaN(wt) ? 0 : wt);
      }, 0);
    },
    weightVariance() {
      const diff = this.totalInputWeight - this.totalNetWeight;
      return diff >= 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2);
    },
    filteredList() {
      let result = [...this.list];

      // Date filtering
      if (this.dateFilter === "month") {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        result = result.filter((item) => {
          if (!item.date) return false;
          const d = new Date(item.date);
          return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
        });
      }

      // Keyword search
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        result = result.filter((item) => {
          const entryNo = ((item.prefix || "") + (item.entryNo || "")).toLowerCase();
          const fromName = (item.fromItem && item.fromItem.itemName ? item.fromItem.itemName : "").toLowerCase();
          const toName = (
            (item.toItem && item.toItem.designId && item.toItem.designId.name) ||
            (item.toItem && item.toItem.itemName) ||
            ""
          ).toLowerCase();
          const toCode = (item.toItem && item.toItem.designId && item.toItem.designId.code ? item.toItem.designId.code : "").toLowerCase();
          const remarks = (item.remarks || "").toLowerCase();

          return (
            entryNo.includes(q) ||
            fromName.includes(q) ||
            toName.includes(q) ||
            toCode.includes(q) ||
            remarks.includes(q)
          );
        });
      }

      return result;
    },
    totalPages() {
      return Math.ceil(this.filteredList.length / this.pageSize) || 1;
    },
    paginatedList() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredList.slice(start, start + this.pageSize);
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    dateFilter() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    // vouchers with several outputs store the total; older ones have one toItem
    outputNetOf(item) {
      if (!item) return 0;
      if (item.outputNetWeight) return Number(item.outputNetWeight);
      return item.toItem && item.toItem.netWeight ? Number(item.toItem.netWeight) : 0;
    },
    getList() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/conversion/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.list = response.data.data || [];
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    formatDate(d) {
      if (!d) return "—";
      const dateObj = new Date(d);
      if (isNaN(dateObj.getTime())) return d;
      return dateObj.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    getMetalClass(cat) {
      if (!cat) return "gold";
      const c = cat.toLowerCase();
      if (c.includes("silver")) return "silver";
      if (c.includes("diamond")) return "diamond";
      if (c.includes("platinum")) return "platinum";
      return "gold";
    },
    openViewModal(item) {
      this.selectedItem = item;
      this.viewDialog = true;
    },
    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    executeDelete() {
      if (!this.itemToDelete) return;
      this.isDeleting = true;
      axios({
        method: "post",
        url: "/conversion/delete",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: { id: this.itemToDelete._id },
      })
        .then((response) => {
          this.isDeleting = false;
          this.deleteDialog = false;
          this.itemToDelete = null;
          this.msg = response.data.msg || "Conversion deleted successfully";
          this.showsnackbar = true;
          if (response.data.status) {
            this.getList();
          }
        })
        .catch((err) => {
          this.isDeleting = false;
          this.deleteDialog = false;
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.conversion-page-container {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 18px 24px 40px 24px;
  background-color: transparent;
  color: #172238;
}

/* 1. Header Section */
.page-hero-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.eyebrow-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #9a6b27;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.diamond-bullet {
  font-size: 13px;
  margin-right: 6px;
  color: #9a6b27;
}

.page-headline {
  font-family: 'Cinzel', 'Inter', serif;
  font-size: 26px;
  font-weight: 700;
  color: #172238;
  letter-spacing: -0.2px;
  line-height: 1.2;
  margin: 0;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

/* Luxury Primary Button */
.luxury-btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  padding: 11px 20px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28);
  transition: all 0.25s ease;
  letter-spacing: 0.2px;
}

.luxury-btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38);
  transform: translateY(-1.5px);
}

.luxury-btn-primary:active {
  transform: translateY(0);
}

/* 2. KPI Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

@media (max-width: 1080px) {
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}

.metric-card {
  background: #ffffff;
  border: 1px solid #eeebe4;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.metric-card:hover {
  border-color: #dfd7cc;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}

.metric-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.metric-icon-box {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-gold {
  background: #fbf5eb;
  border: 1px solid #f2e6d4;
}

.icon-emerald {
  background: #edfbf2;
  border: 1px solid #d9f3e1;
}

.icon-amber {
  background: #fffbeb;
  border: 1px solid #fef3c7;
}

.icon-blue {
  background: #eff6ff;
  border: 1px solid #dbeafe;
}

.metric-chip {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f3f4f6;
  color: #6b7280;
}

.metric-chip.emerald {
  background: #ecfdf5;
  color: #059669;
}

.metric-chip.amber {
  background: #fffbeb;
  color: #d97706;
}

.metric-chip.blue {
  background: #eff6ff;
  color: #2563eb;
}

.metric-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #334155;
  margin-bottom: 4px;
}

.metric-value {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  line-height: 1.1;
  margin-bottom: 6px;
}

.unit-text {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
}

.metric-caption {
  font-size: 11.5px;
  color: #64748b;
  font-weight: 500;
}

/* 3. Toolbar Panel */
.toolbar-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #eeebe4;
  border-radius: 12px;
  padding: 8px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 260px;
  max-width: 480px;
  background: #fbfaf8;
  border: 1px solid #e7e2da;
  border-radius: 8px;
  padding: 6px 12px;
  transition: all 0.2s;
}

.search-input-wrapper:focus-within {
  background: #ffffff;
  border-color: #b9893f;
  box-shadow: 0 0 0 3px rgba(185, 137, 63, 0.12);
}

.custom-search-input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 12.5px;
  color: #172238;
  width: 100%;
}

.custom-search-input::placeholder {
  color: #94a3b8;
}

.clear-search-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 2px;
  display: flex;
  align-items: center;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-pill-group {
  display: flex;
  align-items: center;
  background: #f4efe6;
  padding: 3px;
  border-radius: 8px;
}

.filter-pill {
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.filter-pill.active {
  background: #ffffff;
  color: #9a6b27;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.icon-action-btn {
  background: #fbfaf8;
  border: 1px solid #e7e2da;
  border-radius: 8px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.icon-action-btn:hover {
  background: #ffffff;
  border-color: #b9893f;
  transform: translateY(-1px);
}

/* 4. Luxury Table Card */
.table-card {
  background: #ffffff;
  border: 1px solid #eeebe4;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.02);
}

.table-container {
  overflow-x: auto;
}

.luxury-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.luxury-table thead tr {
  background-color: #fbf9f5;
  border-bottom: 1px solid #eeebe4;
}

.luxury-table th {
  padding: 13px 16px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #64748b;
  text-transform: uppercase;
  white-space: nowrap;
}

.luxury-table tbody tr.table-row {
  border-bottom: 1px solid #f6f3ee;
  transition: background-color 0.15s ease;
}

.luxury-table tbody tr.table-row:hover {
  background-color: #fcfbf8;
}

.luxury-table td {
  padding: 13px 16px;
  font-size: 13px;
  color: #172238;
  vertical-align: middle;
}

/* Table Specific Cell Elements */
.entry-no-badge {
  display: inline-block;
  font-family: 'Plus Jakarta Sans', 'Inter', monospace;
  font-weight: 700;
  font-size: 12.5px;
  color: #9a6b27;
  background: #fdfaf2;
  border: 1px solid #ecd8b8;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.3px;
}

.date-cell {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: #475569;
  white-space: nowrap;
}

.item-primary-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-title {
  font-weight: 600;
  color: #172238;
  font-size: 13px;
}

.item-meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.metal-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 4px;
  letter-spacing: 0.4px;
}

.metal-badge.gold {
  background: #fef8e7;
  color: #b45309;
  border: 1px solid #fde68a;
}

.metal-badge.silver {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.metal-badge.diamond {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.metal-badge.platinum {
  background: #f8fafc;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.design-code-badge {
  font-size: 10.5px;
  color: #8d8a98;
  background: #f3f4f6;
  padding: 1px 5px;
  border-radius: 4px;
  font-family: monospace;
}

.meta-wt {
  font-size: 11px;
  color: #8d8a98;
}

.arrow-indicator {
  width: 28px;
  height: 28px;
  background: #fdfaf4;
  border: 1px solid #eee5d3;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.weight-cell {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 3px;
}

.weight-number {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #172238;
}

.weight-unit {
  font-size: 11px;
  color: #8d8a98;
  font-weight: 500;
}

.remarks-text {
  font-size: 12.5px;
  color: #64748b;
  display: block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.actions-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.row-action-btn {
  background: #fbfaf8;
  border: 1px solid #ebe5db;
  border-radius: 7px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #64748b;
}

.row-action-btn.view-btn:hover {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #2563eb;
  transform: translateY(-1px);
}

.row-action-btn.delete-btn:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
  transform: translateY(-1px);
}

/* Empty State */
.empty-state-cell {
  padding: 50px 20px !important;
}

.empty-state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.empty-icon-circle {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #fbf6ec;
  border: 1px solid #eddcc3;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.empty-title {
  font-family: 'Cinzel', serif;
  font-size: 18px;
  font-weight: 700;
  color: #172238;
  margin: 0 0 6px 0;
}

.empty-desc {
  font-size: 13px;
  color: #8d8a98;
  max-width: 320px;
  margin: 0;
}

/* Table Footer / Pagination */
.table-footer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: #ffffff;
  border-top: 1px solid #eeebe4;
  font-size: 12.5px;
  color: #64748b;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-nav-btn {
  background: #fbfaf8;
  border: 1px solid #e7e2da;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  color: #475569;
}

.page-nav-btn:hover:not(:disabled) {
  background: #ffffff;
  border-color: #b9893f;
  color: #9a6b27;
}

.page-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-indicator {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

/* Modals */
.dialog-content-card {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
}

.dialog-modal-header {
  padding: 18px 20px;
  border-bottom: 1px solid #f0ebe4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fdfaf4;
}

.dialog-header-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fbf5eb;
  border: 1px solid #f2e6d4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-title {
  font-family: 'Cinzel', serif;
  font-size: 17px;
  font-weight: 700;
  color: #172238;
  margin: 0;
}

.dialog-subdate {
  font-size: 11.5px;
  color: #8d8a98;
}

.dialog-close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
}

.flow-cards-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
}

.flow-card {
  background: #fcfbf9;
  border: 1px solid #ebe5db;
  border-radius: 12px;
  padding: 14px;
}

.flow-card.from-card {
  border-top: 3px solid #b45309;
}

.flow-card.to-card {
  border-top: 3px solid #16a34a;
}

.flow-badge {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 4px;
}

.from-badge {
  background: #fffbeb;
  color: #b45309;
}

.to-badge {
  background: #ecfdf5;
  color: #059669;
}

.flow-item-name {
  font-size: 14px;
  font-weight: 700;
  color: #172238;
  margin: 8px 0 10px 0;
}

.flow-item-details {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.detail-label {
  color: #8d8a98;
}

.detail-val {
  color: #172238;
  font-weight: 500;
}

.detail-row.highlight {
  border-top: 1px dashed #e2dcce;
  padding-top: 6px;
  margin-top: 2px;
}

.gold-highlight .bold {
  color: #9a6b27;
  font-size: 13.5px;
}

.flow-divider-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fef8e7;
  border: 1px solid #fde68a;
}

.summary-variance-strip {
  display: flex;
  justify-content: space-around;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  text-align: center;
}

.var-label {
  display: block;
  font-size: 10.5px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
}

.var-val {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #172238;
}

.var-val.delta {
  color: #b45309;
}

.dialog-remarks-box {
  background: #fbfaf8;
  border: 1px solid #eeebe4;
  border-radius: 10px;
  padding: 10px 14px;
}

.remarks-title {
  font-size: 10px;
  font-weight: 700;
  color: #8d8a98;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 2px;
}

.remarks-content {
  font-size: 12.5px;
  color: #475569;
  margin: 0;
}

.dialog-modal-footer {
  padding: 12px 20px;
  border-top: 1px solid #f0ebe4;
  display: flex;
  justify-content: flex-end;
  background: #faf8f5;
}

/* Delete Confirm Dialog */
.warning-icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fee2e2;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-dialog-title {
  font-family: 'Cinzel', serif;
  font-size: 19px;
  font-weight: 700;
  color: #172238;
  margin: 0 0 6px 0;
}

.confirm-dialog-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}

.delete-confirm-btn {
  background: #dc2626 !important;
  color: #ffffff !important;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.25);
}

.delete-confirm-btn:hover:not(:disabled) {
  background: #b91c1c !important;
}

.delete-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
